/**
 * How a number is written for a given organisation - one rule for the whole product.
 *
 * THE CURRENCY DECIDES THE GROUPING. An organisation that trades in rupees reads 31,28,96,850
 * (lakhs and crores); one in dirhams or dollars reads 312,896,850. That is a fact about the
 * organisation, not about the viewer's laptop or the screen the figure happens to be on, so
 * neither the browser locale nor a per-screen choice is allowed to decide it. Every figure the
 * product shows - in a grid, a card, a PDF, an email, a report - goes through here with the
 * organisation's ISO 4217 code, and a count is grouped exactly as its money is.
 *
 * Shared by the frontend and the backend so a screen and the document it prints cannot
 * disagree about where the commas go.
 */
/** Locale that renders each currency the way its market expects (digit grouping). */
const LOCALES = {
    INR: 'en-IN',
    AED: 'en-AE',
    SAR: 'en-SA',
    USD: 'en-US',
    EUR: 'en-IE',
    GBP: 'en-GB',
    SGD: 'en-SG',
    AUD: 'en-AU',
};
/** The symbol (or prefix) each currency prints with - '₹1,00,000', 'AED 100,000'. */
const SYMBOLS = {
    INR: '₹',
    AED: 'AED ',
    SAR: 'SAR ',
    USD: '$',
    EUR: '€',
    GBP: '£',
    SGD: 'S$',
    AUD: 'A$',
};
/** What every caller falls back to when the organisation has no currency on record. */
export const DEFAULT_CURRENCY = 'INR';
/** `'inr'` / `' INR '` / `null` → `'INR'`, so a code from any source lands on the tables. */
export const normaliseCurrency = (code) => (code ?? '').trim().toUpperCase() || DEFAULT_CURRENCY;
/** The BCP 47 locale whose digit grouping this currency's market uses. */
export const localeForCurrency = (code) => LOCALES[normaliseCurrency(code)] ?? 'en-US';
/** The symbol (or prefix) for a currency - '₹', 'AED ', '$', or the code itself for a stranger. */
export const currencySymbolFor = (code) => {
    const normalised = normaliseCurrency(code);
    return SYMBOLS[normalised] ?? `${normalised} `;
};
/**
 * A plain figure - a quantity, a count, a percentage's number - grouped for the currency.
 *
 * `formatCount(1670000, 'INR')` → `16,70,000`; `formatCount(1670000, 'AED')` → `1,670,000`.
 * Without options the fraction behaves as `toLocaleString()` always has (up to three decimals,
 * trailing zeros dropped), so a call moved here changes only its grouping. Non-numbers and
 * NaN print as `0` rather than "NaN": a figure a screen was going to show is a figure, and a
 * broken one is better seen as zero than as a word.
 */
export const formatCount = (value, currency, options = {}) => {
    const amount = Number(value);
    const safe = Number.isFinite(amount) ? amount : 0;
    const fraction = {};
    if (options.decimals !== undefined) {
        fraction.minimumFractionDigits = options.decimals;
        fraction.maximumFractionDigits = options.decimals;
    }
    else if (options.maxDecimals !== undefined) {
        fraction.maximumFractionDigits = options.maxDecimals;
    }
    return safe.toLocaleString(localeForCurrency(currency), fraction);
};
/**
 * Money with its symbol - `₹31,28,96,850.00`, `AED 312,896,850.00`.
 *
 * TWO DECIMALS BY DEFAULT, because an amount is an accounting figure and reads as one: a
 * total of 850 next to one of 850.50 lines up only when both carry their paise. Pass
 * `decimals` to override where a rounded figure is the point (a KPI tile, a chart label).
 */
export const formatMoneyFor = (value, currency, options = {}) => {
    const figure = formatCount(value, currency, {
        decimals: options.decimals ?? (options.maxDecimals === undefined ? 2 : undefined),
        maxDecimals: options.maxDecimals,
    });
    // The sign leads the symbol, as accounts write it: -₹1,600.00, not ₹-1,600.00.
    return figure.startsWith('-') ? `-${currencySymbolFor(currency)}${figure.slice(1)}` : `${currencySymbolFor(currency)}${figure}`;
};
/**
 * The figure alone, as a document's money column prints it - `31,28,96,850.00`, no symbol.
 *
 * Inside a table the currency is noise; it is stated once where it is read (the totals block,
 * the amount in words) and the columns carry figures. Same grouping as everything else.
 */
export const formatAmountFor = (value, currency, decimals = 2) => formatCount(value, currency, { decimals });
//# sourceMappingURL=numberFormat.js.map